import { useEffect, useRef } from 'react';
import { ListPlus, Type, X } from 'lucide-react';
import type { LogLine } from '@/lib/logcat/parser';

/**
 * 选区 payload —— 由 Logs.tsx 在 mouseup 后构造好交给 popover 与 modal。
 *
 * 单行：startLineIdx === endLineIdx，line 为这一行；text 为选区子串。
 * 跨行：startLineIdx !== endLineIdx，可以走"加为序列"路径。
 */
export interface LogSelectionPayload {
  /** 选区起始行（display row 索引）。 */
  startLineIdx: number;
  /** 选区结束行（display row 索引），与 start 相同表示同行。 */
  endLineIdx: number;
  /** 起止行的 LogLine 对象。 */
  startLine: LogLine;
  endLine: LogLine;
  /** 选区原始文本（含换行）。 */
  text: string;
  /** 选区右下角屏幕坐标（用于定位 popover）。 */
  anchor: { x: number; y: number };
  /** 选区跨行时途经的所有行（含起止）。多行 sequence 用。 */
  spannedLines: LogLine[];
}

interface Props {
  payload: LogSelectionPayload;
  onAddKeyword: (text: string, line: LogLine) => void;
  onAddSequence: (lines: LogLine[]) => void;
  onDismiss: () => void;
}

/**
 * Phase 14.1 选区浮动工具条。
 *
 * 显示在选区右下角，按钮：
 *   - 加为关键字（默认；引擎内部走"字面 → 骨架对齐 → 启发式"三段式）
 *   - 跨行 ⇒ 加为序列规则
 *
 * 第一版引擎已下线 regex matcher，相应的「加为正则（已转义）」按钮一并删除。
 */
export function LogSelectionPopover({
  payload,
  onAddKeyword,
  onAddSequence,
  onDismiss,
}: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  // 屏幕外点击 / Esc 自动关闭，但不抢选区文字 — 我们故意不监听 selectionchange
  // 进行自动关闭（用户可能想反复操作选区）。
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss();
    };
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    document.addEventListener('keydown', onKey);
    // mousedown 而非 click：避免把"再次选区"的 mousedown 当作"关闭再打开"
    document.addEventListener('mousedown', onDocClick, { capture: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick, { capture: true });
    };
  }, [onDismiss]);

  const isMultiLine = payload.startLineIdx !== payload.endLineIdx;
  const text = payload.text;
  const previewText = text.length > 80 ? `${text.slice(0, 80)}…` : text;

  // 防止溢出屏幕
  const left = Math.min(payload.anchor.x, window.innerWidth - 320);
  const top = Math.min(payload.anchor.y + 4, window.innerHeight - 160);

  return (
    <div
      ref={ref}
      className="fixed z-40 w-[300px] rounded-lg border border-line bg-bg-card shadow-2xl"
      style={{ left, top }}
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-2 py-1.5 text-[11px]">
        <span className="font-semibold text-ink">
          {isMultiLine
            ? `已选 ${payload.spannedLines.length} 行`
            : '已选片段'}
        </span>
        <button
          className="text-ink-muted hover:text-ink"
          onClick={onDismiss}
          title="关闭"
        >
          <X className="h-3 w-3" />
        </button>
      </header>
      <div className="border-b border-line bg-bg-soft px-2 py-1.5">
        <pre className="whitespace-pre-wrap break-all font-mono text-[10px] leading-snug text-ink">
          {previewText || '<空>'}
        </pre>
      </div>
      <div className="grid grid-cols-1 gap-1 p-1.5 text-[11px]">
        <button
          className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-bg-soft"
          onClick={() => onAddKeyword(text, payload.startLine)}
          disabled={!text.trim()}
          title="把选中片段加为 keyword（引擎自动跑字面 → 骨架对齐 → 启发式）"
        >
          <Type className="h-3.5 w-3.5 text-emerald-400" />
          <span className="flex-1">加为关键字</span>
          <span className="text-ink-dim">默认</span>
        </button>
        {isMultiLine && (
          <button
            className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-bg-soft"
            onClick={() => onAddSequence(payload.spannedLines)}
            title="把跨行选区的每行做成一步，构造序列规则"
          >
            <ListPlus className="h-3.5 w-3.5 text-amber-400" />
            <span className="flex-1">加为序列规则</span>
            <span className="text-ink-dim">{payload.spannedLines.length} 步</span>
          </button>
        )}
      </div>
    </div>
  );
}
